'use client';

import { Kbd } from '@/components/common/Kbd';
import { useFeatureFlag } from '@/domains/common/feature-flags';
import { stageDefs } from '@/domains/pipeline/stages';
import { usePathname, useRouter } from 'next/navigation';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';

type SponsorOption = {
  id: string;
  name: string;
  plan_name: string;
};

type Item =
  | { kind: 'page'; label: string; sub: string; href: string; group: 'Pages' }
  | { kind: 'sponsor'; label: string; sub: string; href: string; group: 'Sponsors' }
  | { kind: 'stage'; label: string; sub: string; href: string; group: string };

const PAGE_ITEMS: Item[] = [
  { kind: 'page', label: 'Today', sub: 'Your daily queue', href: '/', group: 'Pages' },
  { kind: 'page', label: 'Sponsors', sub: 'Plan-year console', href: '/crm', group: 'Pages' },
  {
    kind: 'page',
    label: 'Compliance',
    sub: '3(16) fiduciary dashboard',
    href: '/compliance',
    group: 'Pages',
  },
  { kind: 'page', label: 'Inbox', sub: 'Email threads', href: '/inbox', group: 'Pages' },
];

interface Props {
  sponsors: SponsorOption[];
  onClose: () => void;
}

export const CommandPalette: FunctionComponent<Props> = ({ sponsors, onClose }) => {
  const router = useRouter();
  const pathname = usePathname();
  const kpcOn = useFeatureFlag('kpc_cobranding');
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const sponsorIdInPath = useMemo(() => {
    const match = pathname?.match(/^\/sponsor\/([^/]+)/);
    return match ? match[1] : null;
  }, [pathname]);

  const currentSponsor = sponsorIdInPath ? (sponsors.find(s => s.id === sponsorIdInPath) ?? null) : null;

  const items = useMemo<Item[]>(() => {
    // KPC demo mode relabels Sponsors as Clients; the Form 5500 output stages
    // are hidden from the stage jumper.
    const pageItems: Item[] = PAGE_ITEMS.map(it =>
      kpcOn && it.href === '/crm' ? { ...it, label: 'Clients' } : it,
    );

    const sponsorItems: Item[] = sponsors.map(s => ({
      kind: 'sponsor' as const,
      label: s.name ?? s.plan_name,
      sub: s.plan_name,
      href: `/crm/${s.id}`,
      group: 'Sponsors' as const,
    }));

    const visibleStages = stageDefs.filter(d => d.slug !== 'form-assembly' && d.slug !== 'filing');
    const stageItems: Item[] = currentSponsor
      ? visibleStages.map(d => ({
          kind: 'stage' as const,
          label: d.name,
          sub: `${currentSponsor.name ?? currentSponsor.plan_name} · Stage ${String(d.number).padStart(2, '0')} · ${d.group}`,
          href: `/sponsor/${currentSponsor.id}/stage/${d.slug}`,
          group: `Stages · ${currentSponsor.name ?? currentSponsor.plan_name}`,
        }))
      : [];

    return [...pageItems, ...sponsorItems, ...stageItems];
  }, [sponsors, currentSponsor, kpcOn]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter(it => `${it.label} ${it.sub}`.toLowerCase().includes(needle));
  }, [items, q]);

  const grouped = useMemo(() => {
    const groups = new Map<string, Item[]>();
    filtered.forEach(it => {
      const arr = groups.get(it.group) ?? [];
      arr.push(it);
      groups.set(it.group, arr);
    });
    return Array.from(groups.entries());
  }, [filtered]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setActive(0);
  }, [q]);

  useEffect(() => {
    const node = listRef.current?.querySelector(`[data-idx="${active}"]`);
    node?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function go(it: Item): void {
    router.push(it.href);
    onClose();
  }

  function onKey(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(a => Math.min(a + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(a => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = filtered[active];
      if (target) go(target);
    }
  }

  let runningIdx = 0;

  return (
    <div
      className="fixed inset-0 z-[200] flex animate-[cmdk-fade_0.12s_ease-out] items-start justify-center bg-[rgba(15,17,21,0.42)] px-6 pb-6 pt-[12vh] backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="flex max-h-[70vh] w-full max-w-[560px] animate-[cmdk-pop_0.14s_ease-out] flex-col overflow-hidden rounded-lg border border-line-2 bg-bg-2 shadow-[0_24px_60px_rgba(0,0,0,0.22),0_2px_6px_rgba(0,0,0,0.06)]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-2.5 border-b border-line px-3.5 py-3 text-ink-3">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3-3" />
          </svg>
          <input
            className="flex-1 border-none bg-transparent font-sans text-[14.5px] tracking-[-0.01em] text-ink outline-none placeholder:text-ink-4"
            ref={inputRef}
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={onKey}
            placeholder="Jump to a sponsor, stage, or page…"
            autoComplete="off"
            spellCheck={false}
          />
          <Kbd>esc</Kbd>
        </div>

        <div className="flex-1 overflow-y-auto px-1.5 pb-2 pt-1.5" ref={listRef}>
          {grouped.length === 0 ? (
            <div className="px-3.5 py-7 text-center text-[12.5px] text-ink-4">
              No matches for &quot;{q}&quot;.
            </div>
          ) : (
            grouped.map(([group, list]) => (
              <div className="py-1" key={group}>
                <div className="px-2.5 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-ink-4">
                  {group}
                </div>
                {list.map(it => {
                  const idx = runningIdx++;
                  const isActive = idx === active;
                  return (
                    <button
                      type="button"
                      key={`${it.kind}:${it.href}`}
                      data-idx={idx}
                      className={`flex w-full cursor-pointer items-center gap-2.5 rounded-sm border-none px-2.5 py-2 text-left text-ink transition-colors duration-75 ${
                        isActive ? 'bg-acc-soft' : 'bg-transparent'
                      }`}
                      onMouseEnter={() => setActive(idx)}
                      onClick={() => go(it)}
                    >
                      <span
                        className={`inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-[5px] text-xs ${
                          isActive ? 'bg-bg-2 text-acc' : `bg-bg-hover ${ICON_COLOR[it.kind]}`
                        }`}
                      >
                        {iconFor(it.kind)}
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-px">
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[13px] font-medium tracking-[-0.01em] text-ink">
                          {it.label}
                        </span>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[11.5px] text-ink-3">
                          {it.sub}
                        </span>
                      </span>
                      {isActive ? (
                        <span className="ml-auto shrink-0 pl-2 font-mono text-[11px] text-acc">↵</span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="flex items-center gap-3.5 border-t border-line bg-bg px-3.5 py-2 text-[10.5px] text-ink-4">
          <span className="inline-flex items-center gap-1">
            <Kbd variant="hint">↑</Kbd>
            <Kbd variant="hint">↓</Kbd>
            navigate
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd variant="hint">↵</Kbd>
            select
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd variant="hint">esc</Kbd>
            close
          </span>
        </div>
      </div>
    </div>
  );
};

const ICON_COLOR: Record<Item['kind'], string> = {
  sponsor: 'text-violet',
  stage: 'text-sky',
  page: 'text-ink-3',
};

function iconFor(kind: Item['kind']): string {
  if (kind === 'sponsor') return '◔';
  if (kind === 'stage') return '◇';
  return '⌂';
}
